import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Dispatcher, Events, on, withEventHandlers } from '@ngrx/signals/events';
import { withDevtools, withDevToolsStub, withGlitchTracking, withTrackedReducer } from '@angular-architects/ngrx-toolkit';
import { mapResponse } from '@ngrx/operators';
import { catchError, exhaustMap, filter, map, of, switchMap, take, tap } from 'rxjs';
import { NServicesHttpErrorEnum, NServicesHttpErrorResponse } from '@core/models/custom-http-error.model';
import { AuthService } from '@core/services/auth.service';
import { StorageService } from '@core/services/storage.service';
import { AppErrorState, createLoginError, INITIAL_APP_ERROR_STATE, resetAppError } from '@core/store/core.models';
import { accessEvents, authApiEvents, authPageEvents, sessionEvents } from '@core/store/core.events';
import { CurrentUserState, resetCurrentUser, withCurrentUser } from '@core/store/features/with-current-user';
import { resetSession, SessionState, withSession } from '@core/store/features/with-session';
import { WINDOW } from '@core/tokens';
import { environment } from '@env/environment';
import { TimesheetService } from '@shared/services/timesheet.service';
import { UsersService } from '@shared/services/users.service';

type CoreState = SessionState & CurrentUserState & AppErrorState;

/** Clears all three slices. */
const resetAll = () => [resetSession(), resetCurrentUser(), resetAppError()];

const OAUTH_CALLBACK_PARAMS: string[] = ['code', 'error', 'error_description', 'state', 'session_state'];

/**
 * Drops the OAuth callback parameters from the address bar once they have been read, so the
 * authorization code does not linger in the browser history or leak into error reports.
 * Every other query parameter, the path and the hash are preserved, otherwise deep links
 * carrying their own parameters would break.
 */
const stripOAuthCallbackParams = (window: Window): void => {
  const url: URL = new URL(window.location.href);
  OAUTH_CALLBACK_PARAMS.forEach(param => url.searchParams.delete(param));

  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

/**
 * Root store for the application session: tokens, current user, error state.
 *
 * Every state transition lives in `withTrackedReducer` and is pure; every side effect (HTTP,
 * Router, localStorage) lives in `withEventHandlers`. The only public method is `init()`.
 *
 * CAUTION — do not move `init()` into a `withHooks({ onInit })`. It looks tidier but it
 * reintroduces a DI cycle (CoreStore -> AuthService -> HttpClient -> coreInterceptor ->
 * inject(CoreStore)). `init()` is called explicitly by the app initializer in app.config.ts,
 * once the configuration has been loaded.
 *
 * CAUTION — `Events` is a Subject, not a ReplaySubject: an event dispatched before the store
 * is constructed is silently lost. That is why startup goes through the `init()` method
 * instead of a dispatch made straight from app.config.ts — calling a method requires having
 * injected the store, so the handlers are guaranteed to be subscribed already.
 */
export const CoreStore = signalStore(
  { providedIn: 'root' },
  // withTrackedReducer requires the glitch tracking marker: the stub provides it without
  // shipping the instrumentation to production.
  environment.production ? withDevToolsStub('core') : withDevtools('core', withGlitchTracking()),
  withSession(),
  withCurrentUser(),
  withState<AppErrorState>(INITIAL_APP_ERROR_STATE),
  withComputed(({ userEmail, idToken, accessToken }) => ({
    sessionData: computed(() => ({
      email: userEmail(),
      idToken: idToken(),
      accessToken: accessToken()
    }))
  })),
  withTrackedReducer<CoreState>(
    on(sessionEvents.hydrated, ({ payload }) => ({ ...payload })),

    on(sessionEvents.tokenRefreshed, authApiEvents.tokenGenerated, ({ payload }) => ({ ...payload })),

    on(authApiEvents.userLoaded, ({ payload: user }) => ({
      user,
      loginError: undefined,
      userWorktime: undefined,
      userTimebank: undefined,
      retrieveUserCompleted: true
    })),

    // The retrieval is complete even when it fails: otherwise the guards waiting on
    // `retrieveUserCompleted` would hang forever.
    on(authApiEvents.userLoadFailed, authApiEvents.anonymous, () => ({
      user: undefined,
      userWorktime: undefined,
      userTimebank: undefined,
      retrieveUserCompleted: true
    })),

    // One event, one single patch: the login error and the user reset no longer compete over
    // the `loginError` field.
    on(authApiEvents.loginErrorReceived, ({ payload: loginError }) => ({
      loginError,
      user: undefined,
      userWorktime: undefined,
      userTimebank: undefined,
      retrieveUserCompleted: true
    })),

    on(authApiEvents.worktimeLoaded, ({ payload: userWorktime }) => ({ userWorktime })),

    on(authApiEvents.timebankLoaded, ({ payload: userTimebank }) => ({ userTimebank })),

    on(authApiEvents.logoutCompleted, () => resetAll()),

    // "One event, one patch": the redirect performs the reset and, when triggered by an
    // authentication failure, carries the error to render on the login page.
    on(authPageEvents.loginRedirectRequested, ({ payload: loginError }) => [
      ...resetAll(),
      { loginError, retrieveUserCompleted: true }
    ]),

    // The user is gone: mark the retrieval as done, otherwise the auth/guest/error-page guards
    // waiting on `retrieveUserCompleted` would hang forever.
    on(accessEvents.revoked, ({ payload: errorCode }) => [...resetAll(), { errorCode, retrieveUserCompleted: true }]),

    on(accessEvents.denied, ({ payload: errorCode }) => ({ errorCode })),

    on(authPageEvents.dashboardRequested, () => ({ errorCode: undefined }))
  ),
  withEventHandlers((
    store,
    events = inject(Events),
    window: Window = inject(WINDOW),
    router: Router = inject(Router),
    authService: AuthService = inject(AuthService),
    usersService: UsersService = inject(UsersService),
    timesheetService: TimesheetService = inject(TimesheetService)
  ) => ({

    /**
     * Startup branch. The reducer for `hydrated` has already run — the Dispatcher delivers to
     * reducers synchronously and to handlers on the queueScheduler — so `store.hasToken()`
     * already reflects the tokens read from localStorage.
     */
    checkToken$: events.on(sessionEvents.hydrated).pipe(
      map(() => {
        const params: URLSearchParams = new URL(window.location.href).searchParams;
        const code: string | null = params.get('code');
        const error: string | null = params.get('error');
        const errorDescription: string | null = params.get('error_description');

        if(!!code || !!error) stripOAuthCallbackParams(window);

        if(store.hasToken()) return sessionEvents.tokenAvailable();

        if(!!code) return sessionEvents.codeReceived(code);

        if(!!error) return authApiEvents.loginErrorReceived({
          error,
          errorDescription: errorDescription ?? undefined,
          correlationId: window.crypto?.randomUUID?.() ?? Date.now().toString(36)
        });

        return authApiEvents.anonymous();
      })
    ),

    generateToken$: events.on(sessionEvents.codeReceived).pipe(
      exhaustMap(({ payload: code }) => authService.generateToken(code).pipe(
        mapResponse({
          next: ({ id_token: idToken, access_token: accessToken, refresh_token: refreshToken }) => authApiEvents.tokenGenerated({
            idToken: idToken ?? '',
            accessToken: accessToken ?? '',
            refreshToken: refreshToken ?? ''
          }),
          error: (err: NServicesHttpErrorResponse) => authApiEvents.tokenGenerationFailed(err)
        })
      ))
    ),

    tokenGenerationFailed$: events.on(authApiEvents.tokenGenerationFailed).pipe(
      map(({ payload: err }) => !!err.code && err.error === NServicesHttpErrorEnum.INVALID_NONCE
        ? authPageEvents.loginRedirectRequested(createLoginError(err.error))
        : authApiEvents.userLoadFailed())
    ),

    tokenFailed$: events.on(sessionEvents.tokenExchangeFailed, sessionEvents.tokenRefreshFailed).pipe(
      // The raw AADSTS description is dropped from the UI for the "session expired" copy:
      // keep it in the console for support and diagnosis.
      tap(({ payload: loginError }) => {
        if(!!loginError?.errorDescription) console.warn(
          `[auth] token failed: ${loginError.error} — ${loginError.errorDescription} (correlation: ${loginError.correlationId})`
        );
      }),
      map(({ payload }) => authPageEvents.loginRedirectRequested(payload))
    ),

    loadUser$: events.on(sessionEvents.tokenAvailable, authApiEvents.tokenGenerated).pipe(
      switchMap(() => authService.getUser().pipe(
        mapResponse({
          next: user => authApiEvents.userLoaded(user),
          error: () => authApiEvents.userLoadFailed()
        })
      ))
    ),

    loadWorktime$: events.on(authApiEvents.userLoaded).pipe(
      switchMap(({ payload: user }) => timesheetService.getUserWorktime(user.id).pipe(
        mapResponse({
          next: worktime => authApiEvents.worktimeLoaded(worktime),
          error: () => authApiEvents.worktimeLoaded([])
        })
      ))
    ),

    loadTimebank$: events.on(authApiEvents.worktimeLoaded).pipe(
      map(() => store.userId()),
      filter((userId): userId is number => !!userId),
      switchMap(userId => usersService.getTimebank(userId).pipe(
        take(1),
        mapResponse({
          next: timebank => authApiEvents.timebankLoaded(timebank),
          error: () => authApiEvents.timebankLoaded({ time: 0 })
        })
      ))
    ),

    authorize$: events.on(authPageEvents.authorizeRequested).pipe(
      tap(({ payload: { loginHint, prompt } }) => authService.authorize(loginHint, prompt))
    ),

    logout$: events.on(authPageEvents.logoutRequested).pipe(
      switchMap(() => authService.logout().pipe(
        catchError(() => of(undefined)),
        map(() => authApiEvents.logoutCompleted())
      ))
    ),

    logoutCompleted$: events.on(authApiEvents.logoutCompleted).pipe(
      tap(() => authService.endSession())
    ),

    loginRedirect$: events.on(authPageEvents.loginRedirectRequested).pipe(
      tap(() => {
        authService.clearStorage();
        if(!router.url.includes('login')) router.navigate(['/login']);
      })
    ),

    accessRevoked$: events.on(accessEvents.revoked).pipe(
      tap(() => {
        authService.clearStorage();
        router.navigate(['/error-page']);
      })
    ),

    accessDenied$: events.on(accessEvents.denied).pipe(
      tap(() => {
        if(!router.url.includes('error-page')) router.navigate(['/error-page']);
      })
    ),

    dashboardRedirect$: events.on(authPageEvents.dashboardRequested).pipe(
      tap(() => router.navigate(['']))
    )

  })),
  withMethods((
    store,
    dispatcher: Dispatcher = inject(Dispatcher),
    storageService: StorageService = inject(StorageService)
  ) => ({

    /**
     * Hydrates the tokens from localStorage. The HTTP calls that follow are
     * fire-and-forget: the bootstrap does not wait for them, the guards are gated on
     * `retrieveUserCompleted`.
     */
    init(): void {
      dispatcher.dispatch(sessionEvents.hydrated({
        idToken: storageService.idToken,
        accessToken: storageService.accessToken,
        refreshToken: storageService.refreshToken
      }));
    }

  }))
);
